import { useTranslation, Translation } from 'react-i18next';
import React, { useRef, memo, useCallback, useMemo, useEffect, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import ReactGA4 from 'react-ga4';
import { useDidRecover } from 'react-router-cache-route';
import { useHistory, useLocation } from 'react-router-dom';
import cx from 'clsx';
import { isEmpty } from 'lodash';

import { IconInputSearch } from '@/assets';
import { IconRcMentionProfile } from '@/assets/stream';
import { Loading, NotConnected, SideBox, Segmented, SearchInput, Button } from '@/components';
import { useCurrentUserId, isOfficalAddress } from '@/utils';
import { getGaUserType, useDebounceState, useQueryParams } from '@/hooks';

import { TABS } from '../../const';
import { FeedItem } from '../../types';
import {
  EmptyStream,
  FeedListItem,
  LoadingSkeleton,
  // TopContributors,
  UnionSearchUser,
  HeaderEditor,
} from '../../components';
import { RecommendUsers } from '../../components/RecommendUser';
// import { TrendingApps } from '../../components/TrendingApps';
// import { FeaturedGroupCard } from '../../components/FeaturedGroupCard';
import {
  useFeedList,
  useNewStreamsCount,
  useFeedSearchList,
  useUpdateStream,
  useStreamSearchSuggestionList,
  useStreamSearchSuggestionMention,
} from '../../hooks';
import style from './index.module.scss';
import { usePostFilter } from '../../components/StreamFilter/hooks';
import { PostFilter } from '../../components/StreamFilter';
import { IssueSelect } from '../../components/IssueSelect';
import { PrimeVoteStatus } from '../../components/PrimeVoteCountTip';
import { useCurrentPrimeIssueId, usePrimePostIssues } from '../../components/IssueSelect/hooks';
import { RecommendPostList, RecommendPostListHandle } from '../../components/RecommendList';

const STREAM_TAB: {
  label: React.JSX.Element;
  value: string;
  sortKey: '-read_count' | '-hot_value' | '-create_at';
}[] = [
  {
    label: <Translation>{t => t('ListContainer.11ivjqf')}</Translation>,
    value: 'latest',
    sortKey: '-create_at',
  },
  {
    label: <Translation>{t => t('ListContainer.trending')}</Translation>,
    value: 'trending',
    sortKey: '-hot_value',
  },
  {
    label: <Translation>{t => t('ListContainer.hot')}</Translation>,
    value: 'hot',
    sortKey: '-read_count',
  },
];

const DefaultScrollTop = { following: 0, recommend: 0 };

export const ListContainer = memo(() => {
  const { t } = useTranslation();

  const address = useCurrentUserId();
  // 兼容老链接
  const [mode, setMode] = useQueryParams<string>('tab');
  const [sort, setSort] = useQueryParams<string>('sort');
  const lastScrollTop = useRef<Record<string, number>>(DefaultScrollTop);
  const history = useHistory();
  const location = useLocation();
  const recommendListRef = useRef<RecommendPostListHandle>(null);

  // 配合作 stream cache list 的，当时是点击进入详情页，这时候列表页不要丢失原来的参数，不然 react-query 会用新参再请求一遍
  const [cachedMode, setCachedMode] = useState<string>(mode || TABS[0].mode);

  useEffect(() => {
    if (location.pathname === '/stream' && cachedMode !== TABS[2].mode) {
      setCachedMode(TABS[2].mode);
      return;
    }
    if (location.pathname === '/' && !mode) {
      setCachedMode(TABS[0].mode);
    }
    if (mode && mode !== cachedMode) {
      setCachedMode(mode);
    }
  }, [cachedMode, location.pathname, mode]);

  const [cachedSort, setCachedSort] = useState<string>(sort || STREAM_TAB[0].value);
  const [urlParamsQ, setUrlParams] = useQueryParams('q', '');
  const [searchValue, setSearchValue, setSearchValueNow] = useDebounceState<string>(urlParamsQ);
  const { primeIssueId, setPrimeIssueId } = useCurrentPrimeIssueId();
  const isSearchValueMatchMention = useMemo(() => searchValue?.startsWith('@'), [searchValue]);
  const isSearchValueMatchHashTag = useMemo(() => searchValue?.startsWith('#'), [searchValue]);
  // const { mutate: readGroupPost } = useReadGroupPinnedPost();
  const { filters } = usePostFilter();

  useEffect(() => {
    urlParamsQ && setSearchValue(urlParamsQ);
  }, [setSearchValue, urlParamsQ]);

  useEffect(() => {
    // 兼容老链接
    // https://debank.com/?filter=%7B"types"%3A%5B"draw"%5D%7D&tab=search
    // https://debank.com/stream?filter=%7B"types"%3A%5B"draw"%5D%7D
    // http://debank.com/stream?filter=%7B%22types%22%3A%5B%22draw%22%5D%7D
    if (mode === TABS[2].mode) {
      const search = new URLSearchParams(history.location.search);
      search.delete('tab');
      history.replace(`/stream?${search.toString()}`);
    }
    // eslint-disable-next-line
  }, []);

  const handleSortChanged = useCallback(
    (v: string) => {
      setSort(v);
    },
    [setSort],
  );

  useEffect(() => {
    if (sort && sort !== cachedSort) {
      setCachedSort(sort);
    }
  }, [sort, cachedSort]);

  const {
    result: followingFeed,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetchFirstPage,
    isFetching,
  } = useFeedList({ address });

  // const { data: groupUnreadPinListData } = useGroupUnreadPinList();
  const lastStreamItem = useMemo(() => followingFeed?.[0], [followingFeed]);

  // resort stream with created at
  const lastStreamCreatedAt = useMemo(() => {
    return lastStreamItem?.create_at;
  }, [lastStreamItem]);

  const followingShownList = useMemo(
    () => followingFeed?.filter(x => !x?.article?.is_withdrawn && x?.article?.is_visible),
    [followingFeed],
  );

  const isFollowingEmpty = useMemo(() => !followingShownList?.length, [followingShownList]);

  const isSearchMode = useMemo(() => cachedMode === TABS[2].mode, [cachedMode]);

  const showPrimePostList = useMemo(() => filters?.types?.includes('prime_pick'), [filters?.types]);

  const enabledSearchList = useMemo(() => {
    if (isSearchMode) {
      if (showPrimePostList) {
        return !!primeIssueId;
      } else {
        return true;
      }
    }
    return false;
  }, [isSearchMode, primeIssueId, showPrimePostList]);

  const {
    result: searchFeed,
    isLoading: isSearchLoading,
    isFetchingNextPage: isFetchingSearchNextPage,
    hasNextPage: hasSearchNextPage,
    fetchNextPage: fetchSearchNextPage,
  } = useFeedSearchList({
    q: searchValue,
    // filter选中primePick时不需要传sortBy
    sortBy: !showPrimePostList ? STREAM_TAB.find(i => i.value === cachedSort)?.sortKey! : undefined,
    filters: filters,
    enabled: enabledSearchList,
    issueId: showPrimePostList ? primeIssueId : undefined,
  });

  const { data: postIssues, isLoading: issuesLoading } = usePrimePostIssues({
    enabled: !!isSearchMode && !!showPrimePostList,
  });

  const currentIssue = useMemo(
    () => postIssues?.find(i => String(i?.id) === primeIssueId),
    [postIssues, primeIssueId],
  );

  useEffect(() => {
    // 设置默认第一个issue
    if (postIssues && !primeIssueId) {
      const firstIssueID = postIssues?.[0]?.id;
      setPrimeIssueId(firstIssueID ? String(firstIssueID) : '');
    }
  }, [postIssues, primeIssueId, setPrimeIssueId]);

  useEffect(() => {
    // 清除issueId
    if (!showPrimePostList) {
      setPrimeIssueId('');
    }
  }, [setPrimeIssueId, showPrimePostList]);

  const searchShownList = useMemo(
    () => searchFeed?.filter(x => !x?.article?.is_withdrawn && x?.article?.is_visible),
    [searchFeed],
  );
  const { data: searchSuggestionList } = useStreamSearchSuggestionList(
    searchValue,
    isSearchValueMatchMention || isSearchValueMatchHashTag,
  );
  const { data: mention } = useStreamSearchSuggestionMention(
    searchValue.slice(1),
    isSearchValueMatchMention,
  );

  const isSearchFeedEmpty = useMemo(
    () => !(isSearchLoading || isFetchingSearchNextPage) && !searchShownList?.length,
    [isFetchingSearchNextPage, isSearchLoading, searchShownList?.length],
  );

  const { data: newStreamsCount } = useNewStreamsCount(lastStreamCreatedAt);

  const fetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const fetchMoreSearch = useCallback(() => {
    if (!isSearchMode) {
      //如果search列表获取过的情况下，!isSearchMode的情况下还是会执行这个函数
      return;
    }
    if (hasSearchNextPage && !isFetchingSearchNextPage) {
      fetchSearchNextPage();
    }
  }, [fetchSearchNextPage, hasSearchNextPage, isFetchingSearchNextPage, isSearchMode]);

  const fetchNewStreams = useCallback(() => {
    if (isFetching) {
      return;
    }
    ReactGA4.event({
      category: 'Stream Notification',
      action: `Check_Stream_Notification`,
    });
    refetchFirstPage();
  }, [isFetching, refetchFirstPage]);

  const { updateFeeds, onRangeChanged } = useUpdateStream(cachedMode, {
    [TABS[0].mode]: followingShownList,
    [TABS[2].mode]: searchShownList,
  });

  const handleTabClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const { mode: m } = e.currentTarget.dataset;
      if (!m || m === cachedMode) {
        if (m === cachedMode && m === TABS[1].mode) {
          lastScrollTop.current[TABS[1].mode] = 0;
          queueMicrotask(() => {
            document.documentElement.scrollTop = 0;
          });
          recommendListRef?.current?.refetchFirstPage();
        }
        return;
      }

      if (m === TABS[2].mode) {
        ReactGA4.event({
          category: 'Stream Search Usage',
          action: `Stream_Search_Click`,
        });
        if (!isEmpty(filters)) {
          ReactGA4.event({
            category: 'Stream Search Usage',
            action: `Stream_Filter_Cache_${JSON.stringify(filters)}`,
          });
        }
        history.replace('/stream');
        setSearchValueNow('');
      } else {
        setMode(m);
      }
      // 记录滚动位置
      if (cachedMode !== TABS[2].mode) {
        lastScrollTop.current[cachedMode] = document.documentElement.scrollTop;
      }
      // 滚动到记忆位置
      if (m !== TABS[2].mode) {
        queueMicrotask(() => {
          document.documentElement.scrollTop = lastScrollTop.current?.[m] || 0;
        });
      } else {
        queueMicrotask(() => {
          document.documentElement.scrollTop = 0;
        });
      }
    },
    [cachedMode, filters, history, setMode, setSearchValueNow],
  );

  useEffect(() => {
    return () => {
      document.documentElement.scrollTop = 0;
      lastScrollTop.current = DefaultScrollTop;
    };
  }, [address, searchValue]);

  useDidRecover(() => {
    document.documentElement.scrollTop = 0;
    lastScrollTop.current = DefaultScrollTop;
    updateFeeds();
  });

  const itemContent = useCallback(
    (index, item) => (
      <FeedListItem
        feed={item}
        restParams={{
          source: 'article',
          article_id: item.article.id,
          contributor_id: item.article._contributorId,
        }}
        enableReadFeedback
        enableContributorId
        openDetailInSelf
        disableBorderBottom={index === (followingShownList?.length || 0) - 1}
        showVoteBtn={showPrimePostList && isSearchMode}
        showHideBtn={!isSearchMode}
      />
    ),
    [followingShownList?.length, isSearchMode, showPrimePostList],
  );

  useEffect(() => {
    // stream 被缓存，sidebar切换也要执行此逻辑
    if (location.pathname !== '/') {
      return;
    }
    if (cachedMode === TABS[0].mode) {
      ReactGA4.event({
        category: 'View Stream',
        action: `Stream_Following_${getGaUserType()}`,
      });
    }
    if (cachedMode === TABS[1].mode) {
      ReactGA4.event({
        category: 'View Stream',
        action: `Stream_Recommend_${getGaUserType()}`,
      });
    }
  }, [location.pathname, cachedMode]);

  useEffect(() => {
    if (searchValue) {
      ReactGA4.event({
        category: 'Stream Search Usage',
        action: `Stream_Search_Text`,
      });
    }
  }, [searchValue]);

  const gotoProfile = useCallback(
    mention => {
      const isOfficial = isOfficalAddress(mention?.id);
      history.push(isOfficial ? `official/${mention?.slug}` : `/profile/${mention?.id}`);
    },
    [history],
  );

  const handleSearchChange = useCallback(
    (v: string) => {
      setSearchValue(v);
      setUrlParams(v);
    },
    [setSearchValue, setUrlParams],
  );

  const handleExitSearch = useCallback(() => {
    setSearchValue('');
    history.replace('/');
    setCachedSort(STREAM_TAB[0].value);

    queueMicrotask(() => {
      document.documentElement.scrollTop = lastScrollTop.current?.['following'] || 0;
    });
  }, [setSearchValue, history]);

  // const handleConfirmReadGroupPost = useCallback(
  //   (groupId, articleId) => {
  //     readGroupPost({
  //       id: groupId,
  //       article_id: articleId,
  //     });
  //   },
  //   [readGroupPost],
  // );
  const computeItemKey = useCallback((_, item: FeedItem) => item.id, []);

  const isSearchListLoading = useMemo(
    () => isSearchLoading || isFetchingSearchNextPage || issuesLoading,
    [isFetchingSearchNextPage, isSearchLoading, issuesLoading],
  );

  return (
    <div className={style.stream}>
      <div className={style.streamMain}>
        {/* {!isSearchMode && groupUnreadPinListData?.pin_list ? (
          <Card className={style.groupStreamPinList}>
            {groupUnreadPinListData?.pin_list.map((item, index) => (
              <div className={style.groupStreamItem}>
                <div className={style.groupStreamItemHeader}>
                  <div className={style.groupStreamItemInfo}>
                    <IconRcFeedPined />
                    {t('ListContainer.fksmfy')}
                    <NavLink
                      className={style.groupTag}
                      to={`/group/${item.group.id}`}
                      target={'_self'}>
                      <IconGroupLogoIcon width={12} height={12} />
                      {item.group.name}
                    </NavLink>
                  </div>

                  <Button
                    type="primary"
                    className={style.groupStreamReadBtn}
                    onClick={() => {
                      handleConfirmReadGroupPost(item.group.id, item.article.id);
                    }}>
                    {t('ListContainer.1ii7q')}
                  </Button>
                </div>
                <div
                  onClick={() => {
                    handleConfirmReadGroupPost(item.group.id, item.article.id);
                  }}>
                  {itemContent(index, item)}
                </div>
              </div>
            ))}
          </Card>
        ) : null} */}
        <div className={style.streamContent}>
          <div className={cx(style.tabs, isSearchMode && style.isSearchModeTabs)}>
            {isSearchMode ? (
              <>
                <div className={style.searchBar}>
                  <SearchInput
                    className={style.searchInput}
                    placeholder={t('ListContainer.searchHolder')}
                    value={searchValue}
                    onChange={handleSearchChange}
                  />
                  <Button className={style.cancelButton} onClick={handleExitSearch} type="gray">
                    {t('ListContainer.x9d2fu')}
                  </Button>
                </div>
                <PostFilter />
                {searchSuggestionList?.length ? (
                  <div className={style.searchSuggestion}>
                    {searchSuggestionList?.map((suggestion, i) => (
                      <div
                        key={suggestion?.id}
                        className={style.searchSuggestionItem}
                        onClick={() => handleSearchChange(suggestion?.raw_string)}>
                        {suggestion.raw_string}
                      </div>
                    ))}
                  </div>
                ) : null}
                {isSearchValueMatchMention && mention ? (
                  <div
                    className={cx(style.searchSuggestion, style.userSearch)}
                    onClick={() => gotoProfile(mention)}>
                    <UnionSearchUser data={mention} />
                    <div className={style.viewProfile}>
                      {t('ListContainer.1pl6yxa')}
                      <IconRcMentionProfile />
                    </div>
                  </div>
                ) : null}
                <div
                  className={cx(
                    style.streamTabItemWrap,
                    isSearchMode && !isSearchFeedEmpty && !showPrimePostList && style.isVisible,
                  )}>
                  <Segmented
                    color="gray"
                    hasSplit={false}
                    options={STREAM_TAB}
                    value={cachedSort}
                    onChange={handleSortChanged}
                    className={style.streamTabItem}
                  />
                </div>
              </>
            ) : (
              <>
                {TABS.slice(0, 2).map(x => (
                  <div
                    className={style.tabItem}
                    key={x.mode}
                    data-mode={x.mode}
                    onClick={handleTabClick}>
                    <div className={cx(style.tabInner, cachedMode === x.mode && style.isSelected)}>
                      {x.label}
                    </div>
                  </div>
                ))}
                <IconInputSearch
                  data-mode={'search'}
                  onClick={handleTabClick as any}
                  className={style.searchIconButton}
                />
              </>
            )}
          </div>
          <div className={cx(style.streamListWrap, cachedMode === TABS[0].mode && style.isVisible)}>
            <HeaderEditor />
            {address ? (
              <>
                {Number(newStreamsCount) > 0 ? (
                  <div className={style.newStreamsCount} onClick={fetchNewStreams}>
                    {isFetching ? (
                      <Loading />
                    ) : (
                      t('ListContainer.showNewFeeds', { count: newStreamsCount })
                    )}
                  </div>
                ) : null}
                <div className={style.streamList}>
                  {followingShownList?.length ? (
                    <Virtuoso
                      className={style.followingList}
                      useWindowScroll
                      data={followingShownList}
                      overscan={10}
                      endReached={fetchMore}
                      itemContent={itemContent}
                      rangeChanged={onRangeChanged.following}
                      computeItemKey={computeItemKey}
                    />
                  ) : null}
                  {isLoading || isFetchingNextPage ? (
                    <LoadingSkeleton />
                  ) : isFollowingEmpty ? (
                    <RecommendUsers />
                  ) : null}
                </div>
              </>
            ) : (
              <NotConnected withIcon className={cx(style.streamContent, style.noConnected)} />
            )}
          </div>
          <div className={cx(style.streamListWrap, cachedMode === TABS[1].mode && style.isVisible)}>
            <RecommendPostList ref={recommendListRef} />
          </div>
          {isSearchMode && showPrimePostList && postIssues?.length ? (
            <div className={style.primePostListHeader}>
              <IssueSelect value={primeIssueId} onChange={setPrimeIssueId} />
              <PrimeVoteStatus issue={currentIssue} />
            </div>
          ) : null}
          <div className={cx(style.streamListWrap, isSearchMode && style.isVisible)}>
            <div className={style.streamList}>
              {searchShownList?.length ? (
                <Virtuoso
                  useWindowScroll
                  data={searchShownList}
                  overscan={10}
                  endReached={fetchMoreSearch}
                  itemContent={itemContent}
                  rangeChanged={onRangeChanged.search}
                />
              ) : null}
              {isSearchListLoading ? (
                <LoadingSkeleton />
              ) : isSearchFeedEmpty ? (
                <EmptyStream
                  address={address}
                  placeholder={showPrimePostList ? 'No PrimePost picked yet' : 'No results found.'}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {/* <StickySide className={style.sidebar}>
        <div className={style.topContributors}>
          <TopContributors />
        </div>
        <FeaturedGroupCard />
        <TrendingApps />
      </StickySide> */}

      <SideBox />
    </div>
  );
});
